import { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Camera, 
  Upload, 
  X, 
  RotateCw, 
  ZoomIn, 
  ZoomOut,
  Check,
  Trash2,
  Image as ImageIcon
} from 'lucide-react';
import { useAvatar } from '@/hooks/useAvatar';
import { useToast } from '@/hooks/use-toast';

interface AvatarUploadProps {
  userName: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showUploadButton?: boolean;
  className?: string;
}

const AvatarUpload = ({ 
  userName, 
  size = 'md', 
  showUploadButton = true,
  className = ""
}: AvatarUploadProps) => {
  const { avatar, uploadAvatar, removeAvatar } = useAvatar();
  const { toast } = useToast();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  
  const imgRef = useRef<HTMLImageElement>(null);
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null);
  const blobUrlRef = useRef('');

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'w-8 h-8';
      case 'md': return 'w-12 h-12';
      case 'lg': return 'w-16 h-16';
      case 'xl': return 'w-32 h-32';
      default: return 'w-12 h-12';
    }
  };

  const getInitials = () => {
    return userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: "Arquivo muito grande",
          description: "Por favor, selecione uma imagem menor que 5MB.",
          variant: "destructive"
        });
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result?.toString() || '');
        setIsDialogOpen(true);
      });
      reader.readAsDataURL(file);
    }
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: false,
    noClick: true
  });

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const cropSize = Math.min(width, height) * 0.8;
    const x = (width - cropSize) / 2;
    const y = (height - cropSize) / 2;
    
    setCrop({
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x,
      y
    });
  }, []);

  function getCroppedImg(
    image: HTMLImageElement,
    crop: PixelCrop,
    scale = 1,
    rotate = 0
  ): Promise<Blob | null> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;

    const rotateRads = rotate * (Math.PI / 180);
    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;

    ctx.save();

    ctx.translate(-cropX, -cropY);
    ctx.translate(centerX, centerY);
    ctx.rotate(rotateRads);
    ctx.scale(scale, scale);
    ctx.translate(-centerX, -centerY);
    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  }

  const handleCropComplete = async () => {
    if (!imgRef.current) {
      toast({
        title: "Erro",
        description: "Imagem não carregada. Tente novamente.",
        variant: "destructive"
      });
      return;
    }

    // Se não há crop definido, usar a imagem toda
    const cropToUse = completedCrop || {
      x: 0,
      y: 0,
      width: imgRef.current.width,
      height: imgRef.current.height,
      unit: 'px' as const
    };

    try {
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        cropToUse,
        scale,
        rotate
      );

      if (croppedImageBlob) {
        const file = new File([croppedImageBlob], 'avatar.jpg', {
          type: 'image/jpeg'
        });

        await uploadAvatar(file);
        setIsDialogOpen(false);
        setImageSrc('');
      }
    } catch (error) {
      console.error('Error cropping image:', error);
      toast({
        title: "Erro no recorte",
        description: "Não foi possível processar a imagem.",
        variant: "destructive"
      });
    }
  };

  const resetCrop = () => {
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    });
    setScale(1);
    setRotate(0);
  };

  return (
    <>
      <div className={`relative group ${className}`} {...getRootProps()}>
        <input {...getInputProps()} />
        
        <Avatar className={`${getSizeClasses()} ring-2 ring-primary/20 transition-all group-hover:ring-primary/40`}>
          <AvatarImage src={avatar.url || ''} alt={userName} />
          <AvatarFallback className="bg-primary text-primary-foreground font-bold">
            {avatar.loading ? '...' : getInitials()}
          </AvatarFallback>
        </Avatar>

        {showUploadButton && (
          <Button
            size="icon"
            variant="secondary"
            className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              document.querySelector<HTMLInputElement>('input[type="file"]')?.click();
            }}
            disabled={avatar.uploading}
          >
            {avatar.uploading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
          </Button>
        )}

        {isDragActive && (
          <div className="absolute inset-0 bg-primary/20 rounded-full flex items-center justify-center border-2 border-dashed border-primary">
            <Upload className="h-6 w-6 text-primary" />
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent 
          className="max-w-2xl z-[100] avatar-upload-modal" 
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Editar Foto de Perfil</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {imageSrc && (
              <div className="space-y-4">
                <div className="relative max-h-96 overflow-hidden rounded-lg border">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop
                  >
                    <img
                      ref={imgRef}
                      alt="Crop me"
                      src={imageSrc}
                      style={{
                        transform: `scale(${scale}) rotate(${rotate}deg)`,
                        maxHeight: '400px',
                        width: '100%',
                        objectFit: 'contain'
                      }}
                      onLoad={onImageLoad}
                    />
                  </ReactCrop>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                      disabled={scale <= 0.5}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    
                    <span className="text-sm text-muted-foreground min-w-[60px] text-center">
                      {Math.round(scale * 100)}%
                    </span>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setScale(Math.min(3, scale + 0.1))}
                      disabled={scale >= 3}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setRotate((rotate - 90) % 360)}
                    >
                      <RotateCw className="h-4 w-4 scale-x-[-1]" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setRotate((rotate + 90) % 360)}
                    >
                      <RotateCw className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      onClick={resetCrop}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <div className="flex gap-2">
                {avatar.url && (
                  <Button
                    variant="outline"
                    onClick={removeAvatar}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover Foto
                  </Button>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    setImageSrc('');
                    resetCrop();
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCropComplete();
                  }}
                  disabled={avatar.uploading}
                  className="bg-primary hover:bg-primary/90 relative z-[110]"
                  type="button"
                >
                  {avatar.uploading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Salvar Foto
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AvatarUpload;